import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';

export const CERTIFICATION_PROGRESS_QUERY_KEYS = {
  byUserAndCase: (userId: string, caseId: string) =>
    ['certification_progress', 'user', userId, 'case', caseId] as const,
  bestPassed: (userId: string, caseId: string) =>
    ['certification_progress', 'best_passed', 'user', userId, 'case', caseId] as const,
  byCase: (caseId: string) => ['certification_progress', 'case', caseId] as const,
};

export const useCertificationProgress = (userId: string | null, caseId: string | null) => {
  return useQuery({
    queryKey: CERTIFICATION_PROGRESS_QUERY_KEYS.byUserAndCase(userId || '', caseId || ''),
    queryFn: async () => {
      if (!userId || !caseId) throw new Error('User ID and Case ID are required');

      const { data, error } = await supabase
        .from('certification_progress')
        .select('*')
        .eq('user_id', userId)
        .eq('case_id', caseId)
        .order('attempt_number', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!userId && !!caseId,
  });
};

export const useBestPassedCertification = (userId: string | null, caseId: string | null) => {
  return useQuery({
    queryKey: CERTIFICATION_PROGRESS_QUERY_KEYS.bestPassed(userId || '', caseId || ''),
    queryFn: async () => {
      if (!userId || !caseId) throw new Error('User ID and Case ID are required');

      const { data, error } = await supabase
        .from('certification_progress')
        .select('*')
        .eq('user_id', userId)
        .eq('case_id', caseId)
        .eq('passed', true)
        .order('score', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!userId && !!caseId,
  });
};

export const useInsertCertificationProgress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      user_id: string;
      case_id: string;
      attempt_number: number;
      current_question?: number;
      answers?: any;
      score?: number;
      passed?: boolean;
      completed_at?: string;
    }) => {
      const { data: result, error } = await supabase
        .from('certification_progress')
        .insert(data)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: CERTIFICATION_PROGRESS_QUERY_KEYS.byUserAndCase(data.user_id, data.case_id),
      });
      void queryClient.invalidateQueries({
        queryKey: CERTIFICATION_PROGRESS_QUERY_KEYS.byCase(data.case_id),
      });
    },
  });
};

export const useUpdateCertificationProgress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: {
        current_question?: number;
        answers?: any;
        score?: number;
        passed?: boolean;
        completed_at?: string;
      };
    }) => {
      const { data, error } = await supabase
        .from('certification_progress')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: CERTIFICATION_PROGRESS_QUERY_KEYS.byUserAndCase(data.user_id, data.case_id),
      });
      void queryClient.invalidateQueries({
        queryKey: CERTIFICATION_PROGRESS_QUERY_KEYS.byCase(data.case_id),
      });
    },
  });
};

export const useDeleteCertificationProgress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, caseId }: { userId: string; caseId: string }) => {
      const { error } = await supabase
        .from('certification_progress')
        .delete()
        .eq('user_id', userId)
        .eq('case_id', caseId)
        .is('completed_at', null);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: CERTIFICATION_PROGRESS_QUERY_KEYS.byUserAndCase(
          variables.userId,
          variables.caseId
        ),
      });
    },
  });
};
